import React, { useRef, useEffect, useLayoutEffect, useState, useMemo } from 'react';
import { useGLTF, useAnimations } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';

const AnimatedModel = ({
  position = [-0.1, 0.3, 0],
  rotation = [0, 0, 0],
  scale = [1.5, 1.5, 1.5],
  mousePosition,
  camera,
  onColorChange,
  isMobile = false,
  lightOffset = [0, -0.1, 0.05] 
}) => {
  const { scene, animations } = useGLTF('/Human1.gltf');
  const { actions, mixer } = useAnimations(animations, scene);
  
  const headRef = useRef();
  const rightHandRef = useRef(); 
  const modelRef = useRef();
  const lightGroupRef = useRef();
  const orbitTimeRef = useRef(0);
  
  // Material and Geometry Refs
  const pointLightRef = useRef();
  const coreMatRef = useRef();
  const coreMeshRef = useRef();
  const ring1Ref = useRef();
  const ring2Ref = useRef();
  const ring3Ref = useRef();
  const glowShaderRef = useRef();
  
  const [isAnimating, setIsAnimating] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [isPosed, setIsPosed] = useState(false);
  
  // Animation Phases: 'idle' | 'hand' (attached) | 'orbit' (floating around)
  const [orbPhase, setOrbPhase] = useState('idle'); 
  const [clickAction, setClickAction] = useState(null);

  const offsetVector = useMemo(() => new THREE.Vector3(...lightOffset), [lightOffset]);

  useEffect(() => () => {
    document.body.style.cursor = '';
  }, []);

  useLayoutEffect(() => {
    if (animations.length > 0) {
      const headBoneName = 'CC_Base_Head';
      // Export order is not a contract. Prefer the named idle and retain the
      // old index only as a fallback for earlier versions of this asset.
      const originalClip = animations.find((clip) => /idle/i.test(clip.name))
        || animations[2]
        || animations[0];

      const filteredTracks = originalClip.tracks.filter(
        (track) => !track.name.includes(headBoneName)
      );

      const newClip = new THREE.AnimationClip(
        originalClip.name,
        originalClip.duration,
        filteredTracks
      );

      const createdClickAction = mixer.clipAction(newClip, scene);
      setClickAction(createdClickAction);
      createdClickAction.reset().play();

      // AnimationAction.play() only schedules the action; without an update,
      // the first painted frame still shows the exported bind pose and the
      // character visibly changes shape when the idle starts. Apply frame zero
      // during layout and keep the primitive hidden until that pose exists.
      mixer.update(0);

      scene.traverse((child) => {
        if (child.isBone) {
          if (child.name === headBoneName) headRef.current = child;
          if (child.name.includes('R_Hand') || child.name.includes('RightHand')) rightHandRef.current = child;
        }
      });

      setIsPosed(true);
      return () => {
        createdClickAction.stop();
        mixer.uncacheAction(newClip, scene);
      };
    }
    setIsPosed(true);
    return undefined;
  }, [animations, mixer, scene]);

  useEffect(() => {
    const currentRotation = { x: 0, y: 0 };
    const handleLookAtMouse = () => {
      if (!headRef.current || !mousePosition?.current) return;

      const { x, y } = mousePosition.current;
      const xNorm = isMobile ? x : x - 0.35;

      const targetX = THREE.MathUtils.clamp(-y * 0.5, -0.5, 0.5);
      const targetY = THREE.MathUtils.clamp(xNorm * 0.5, -0.5, 0.5);

      currentRotation.x = THREE.MathUtils.lerp(currentRotation.x, targetX, 0.1);
      currentRotation.y = THREE.MathUtils.lerp(currentRotation.y, targetY, 0.1);

      headRef.current.rotation.x = currentRotation.x;
      headRef.current.rotation.y = currentRotation.y;
      headRef.current.updateMatrixWorld(true);
    };

    const interval = setInterval(handleLookAtMouse, 16);
    return () => clearInterval(interval);
  }, [mousePosition, isMobile]);

  // Main Animation Loop for the Orb
  useFrame((state, delta) => {
    const t = state.clock.getElapsedTime();

    // The model itself provides the affordance: a very small lift/scale and a
    // pointer cursor on hover. Nothing is drawn over the character or scene.
    if (modelRef.current) {
      const hoverScale = hovered ? 1.025 : 1;
      modelRef.current.scale.x = THREE.MathUtils.lerp(modelRef.current.scale.x, scale[0] * hoverScale, delta * 10);
      modelRef.current.scale.y = THREE.MathUtils.lerp(modelRef.current.scale.y, scale[1] * hoverScale, delta * 10);
      modelRef.current.scale.z = THREE.MathUtils.lerp(modelRef.current.scale.z, scale[2] * hoverScale, delta * 10);
      modelRef.current.position.y = THREE.MathUtils.lerp(modelRef.current.position.y, position[1] + (hovered ? 0.035 : 0), delta * 10);
    }

    // 1. Position Logic based on Phase
    if (orbPhase === 'hand' && rightHandRef.current && lightGroupRef.current) {
      const targetWorldPos = offsetVector.clone().applyMatrix4(rightHandRef.current.matrixWorld);
      lightGroupRef.current.parent.worldToLocal(targetWorldPos);
      lightGroupRef.current.position.copy(targetWorldPos);
      
      orbitTimeRef.current = 0; // Reset the orbit timer while holding it
      
    } else if (orbPhase === 'orbit' && lightGroupRef.current) {
      orbitTimeRef.current += delta; 
      const ot = orbitTimeRef.current; // Time spent in orbit phase
      
      const [charX, charY, charZ] = position; 
      
      // Expand radius smoothly over the first 2 seconds from 0.2 (near hand) to 1.0
      const orbitRadius = THREE.MathUtils.lerp(0.2, 1.0, Math.min(ot / 2.0, 1.0)); 
      
      // Raise height smoothly over 1.5 seconds from the hand's level
      const targetHeight = charY + 2.5 + Math.sin(t * 2) * 0.15; 
      const startingHeight = charY + 0.4; 
      const orbitHeight = THREE.MathUtils.lerp(startingHeight, targetHeight, Math.min(ot / 1.5, 1.0));
      
      const orbitSpeed = 1.2;
      
      const targetX = charX + Math.cos(t * orbitSpeed) * orbitRadius;
      const targetZ = charZ + Math.sin(t * orbitSpeed) * orbitRadius;
      
      const orbitTarget = new THREE.Vector3(targetX, orbitHeight, targetZ);
      
      // Start with a soft lerp so it doesn't jerk out of the hand, then speed up
      const lerpSpeed = THREE.MathUtils.lerp(1.5, 4.0, Math.min(ot, 1.0));
      lightGroupRef.current.position.lerp(orbitTarget, delta * lerpSpeed);
      
    } else {
      orbitTimeRef.current = 0; // Reset when idle
    }

    // 2. Visual Rotation & Pulsing
    if (ring1Ref.current) ring1Ref.current.rotation.x += delta * 2;
    if (ring2Ref.current) ring2Ref.current.rotation.y += delta * 2.5;
    if (ring3Ref.current) ring3Ref.current.rotation.z += delta * 3;
    
    if (coreMeshRef.current) {
      const pulse = 1 + Math.sin(t * 8) * 0.15; 
      coreMeshRef.current.scale.set(pulse, pulse, pulse);
    }

    // 3. Fade In/Out Logic
    const isVisible = orbPhase !== 'idle';
    const targetIntensity = isVisible ? 3 : 0;
    const targetOpacity = isVisible ? 1 : 0;

    if (pointLightRef.current) {
      pointLightRef.current.intensity = THREE.MathUtils.lerp(pointLightRef.current.intensity, targetIntensity, delta * 8);
    }
    
    if (coreMatRef.current) {
      coreMatRef.current.opacity = THREE.MathUtils.lerp(coreMatRef.current.opacity, targetOpacity, delta * 8);
    }
    
    if (lightGroupRef.current) {
      lightGroupRef.current.children.forEach(child => {
        // Fade the rings
        if (child.material && child.material !== coreMatRef.current && child.material.type === 'MeshBasicMaterial') {
          child.material.opacity = THREE.MathUtils.lerp(child.material.opacity, targetOpacity * 0.6, delta * 8);
        }
      });
    }

    // Fade the custom feathered shader
    if (glowShaderRef.current) {
      glowShaderRef.current.uniforms.globalOpacity.value = THREE.MathUtils.lerp(
        glowShaderRef.current.uniforms.globalOpacity.value,
        targetOpacity,
        delta * 8
      );
    }
  });

  const handleClick = () => {
    if (!clickAction || isAnimating) return;

    setIsAnimating(true);
    if (onColorChange) onColorChange();

    setTimeout(() => setOrbPhase('hand'), 3500);
    setTimeout(() => setOrbPhase('orbit'), 4000);

    const touchAction = actions['Touch Thing']; 
    const greetAction = actions['Greet']; 

    if (touchAction && greetAction) {
      clickAction.crossFadeTo(touchAction.reset().fadeIn(0.5), 0.5).play();
      touchAction.loop = THREE.LoopOnce;
      touchAction.clampWhenFinished = true;

      mixer.addEventListener('finished', function onFinished(e) {
        if (e.action === touchAction) {
          touchAction.fadeOut(0.5);
          greetAction.reset().fadeIn(0.5).play();
          greetAction.loop = THREE.LoopOnce;
          greetAction.clampWhenFinished = true;
        }
        
        if (e.action === greetAction) {
          greetAction.fadeOut(0.5); 
          clickAction.reset().fadeIn(0.5).play(); 
          setIsAnimating(false);
          setOrbPhase('idle'); 
          mixer.removeEventListener('finished', onFinished); 
        }
      });
    }
  };

  return (
    <group>
      <primitive
        ref={modelRef}
        object={scene}
        position={position}
        rotation={rotation}
        scale={scale}
        visible={isPosed}
        onClick={handleClick}
        onPointerOver={(event) => {
          event.stopPropagation();
          setHovered(true);
          document.body.style.cursor = 'pointer';
        }}
        onPointerOut={() => {
          setHovered(false);
          document.body.style.cursor = '';
        }}
      />
      
      {/* Sci-Fi Energy Orb */}
      <group ref={lightGroupRef}>
        <pointLight ref={pointLightRef} color="#06b6d4" distance={4} intensity={0} />
        
        {/* Inner Glowing Core */}
        <mesh ref={coreMeshRef}>
          <sphereGeometry args={[0.035, 32, 32]} />
          <meshBasicMaterial ref={coreMatRef} color="#ffffff" transparent={true} opacity={0} toneMapped={false} />
        </mesh>

        {/* Rotating Energy Rings */}
        <mesh ref={ring1Ref}>
          <torusGeometry args={[0.06, 0.004, 16, 32]} />
          <meshBasicMaterial color="#6ea5ff" transparent={true} opacity={0} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
        </mesh>
        <mesh ref={ring2Ref} rotation={[Math.PI / 4, 0, 0]}>
          <torusGeometry args={[0.07, 0.004, 16, 32]} />
          <meshBasicMaterial color="#a5b4fc" transparent={true} opacity={0} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
        </mesh>
        <mesh ref={ring3Ref} rotation={[0, Math.PI / 4, 0]}>
          <torusGeometry args={[0.08, 0.003, 16, 32]} />
          <meshBasicMaterial color="#8b5cf6" transparent={true} opacity={0} blending={THREE.AdditiveBlending} depthWrite={false} toneMapped={false} />
        </mesh>

        {/* Feathered Glow Sphere (Replaces the flat sprite) */}
        <mesh>
          <sphereGeometry args={[0.12, 32, 32]} />
          <shaderMaterial
            ref={glowShaderRef}
            transparent={true}
            depthWrite={false}
            blending={THREE.AdditiveBlending}
            toneMapped={false}
            uniforms={{
              glowColor: { value: new THREE.Color("#6ea5ff") },
              globalOpacity: { value: 0.0 } // Controlled by useFrame
            }}
            vertexShader={`
              varying vec3 vNormal;
              void main() {
                vNormal = normalize(normalMatrix * normal);
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
              }
            `}
            fragmentShader={`
              uniform vec3 glowColor;
              uniform float globalOpacity;
              varying vec3 vNormal;
              void main() {
                float intensity = pow(0.6 - dot(vNormal, vec3(0.0, 0.0, 1.0)), 2.0);
                intensity = clamp(intensity, 0.0, 1.0);
                gl_FragColor = vec4(glowColor * intensity, intensity * globalOpacity);
              }
            `}
          />
        </mesh>
      </group>
    </group>
  );
};

export default AnimatedModel;
